import { color, severityBand } from '../tokens';
import { useSynth, useSynthNav } from '../SynthesisContext';
import { asText, cleanLabel } from '../asText';
import { countOf } from '../../../utils/pluralize';
import { normalizeWorkflowArchitecture } from '../waNormalize';
import { normalizeSentiment } from '../sentiment';

function validSpeaker(v: string): boolean {
  const s = asText(v).trim();
  if (!s) return false;
  return !/^(unknown|n\/a|na|null|undefined|-|—)$/i.test(s);
}

export default function ExecSummary() {
  const { engagement, themes, insights, opportunities, quotes, workflowArchitecture: rawWa, journey, firstPrinciples } = useSynth();
  const workflowArchitecture = normalizeWorkflowArchitecture(rawWa || { architecture: [], oversight: [], diagnostic: '', total_findings: 0, multi_valued_rate: 0, ai_component: null });
  const { setActive, setExpandedThemeRank } = useSynthNav();

  const stakeholdersFromQuotes = new Set(
    quotes.map((q) => asText(q.who).trim()).filter(validSpeaker),
  ).size;
  const stakeholderCount = engagement.stakeholders || stakeholdersFromQuotes || 0;

  const topThemes = themes.slice().sort((a, b) => (a.rank || 0) - (b.rank || 0)).slice(0, 5);
  const diag = asText(workflowArchitecture?.diagnostic || '')
    .replace(/findings/gi, 'observations')
    .replace(/finding /gi, 'observation ');
  const hotLayers = [
    ...(workflowArchitecture?.architecture || []),
    ...(workflowArchitecture?.oversight || []),
  ].filter((l) => !!l.hot);
  const worstStages = journey
    .filter((s) => typeof s.sentiment === 'number')
    .slice()
    .sort((a, b) => normalizeSentiment(a.sentiment) - normalizeSentiment(b.sentiment))
    .slice(0, 3);
  const step1 = firstPrinciples.find((s) => s.n === 1);
  const simplifyBlock = step1?.blocks?.find((b) => asText(b.label).toLowerCase().includes('simplify'));
  const simplifyItems = (simplifyBlock?.items || [])
    .filter((it: any) => !(it && typeof it === 'object' && it.muted))
    .map((it: any) => asText(it))
    .filter(Boolean)
    .slice(0, 3);
  const quickWins = opportunities.filter((o) => o.effort === 'L' && (o.impact === 'H' || o.impact === 'M'));
  const strategicBets = opportunities.filter((o) => o.impact === 'H' && o.effort !== 'L');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>EXECUTIVE SUMMARY</span>
      </div>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
        Synthesis overview
      </h2>
      <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
        Across {countOf(engagement.interviews, 'interview')} with {countOf(stakeholderCount, 'stakeholder')}, this run surfaced {countOf(themes.length, 'theme')}, {countOf(insights.length, 'insight')}, and {countOf(opportunities.length, 'opportunity')}.
      </p>
      <div style={{ height: 1, background: color.hairline, margin: '18px 0' }} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { value: engagement.interviews, label: 'interviews conducted' },
          { value: stakeholderCount || '—', label: 'stakeholders heard' },
          { value: themes.length, label: 'themes surfaced' },
          { value: opportunities.length, label: 'opportunities identified' },
        ].map((s) => (
          <div key={s.label} style={{ background: color.surface, border: `1px solid ${color.hairline}`, borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: color.ink }}>{s.value}</div>
            <div style={{ marginTop: 2, fontSize: 12, color: color.textMuted }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: color.ink, marginBottom: 10 }}>1. What We Found</div>
        {diag && (
          <div style={{ background: color.pillActiveBg, border: `1px solid ${color.chipBorder}`, borderRadius: 10, padding: '12px 14px', marginBottom: 12, fontSize: 13, color: color.text, lineHeight: 1.5 }}>
            Across interviews and provided materials, {diag.charAt(0).toLowerCase()}{diag.slice(1)}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {topThemes.map((t) => {
            const sev = severityBand[t.sev] || severityBand.MED;
            const supportingQuote = quotes.find((q) => q.themeIdx === t.rank);
            return (
              <button
                key={t.rank}
                onClick={() => { setExpandedThemeRank(t.rank); setActive('key-themes'); }}
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  border: `1px solid ${color.hairline}`,
                  background: color.surface,
                  borderRadius: 10,
                  padding: '12px 14px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: color.purple }}>#{String(t.rank).padStart(2, '0')}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: color.ink, flex: 1 }}>{cleanLabel(t.name)}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, background: sev.bg, color: sev.fg, border: `1px solid ${sev.border}`, borderRadius: 6, padding: '2px 7px' }}>
                    {t.sev}
                  </span>
                  <span style={{ fontSize: 11, color: color.textMuted }}>{t.mentions}/{stakeholderCount || engagement.interviews} stakeholders</span>
                </div>
                {supportingQuote && (
                  <div style={{ marginTop: 7, marginLeft: 28, fontSize: 12, color: color.textMuted, fontStyle: 'italic' }}>
                    "{asText(supportingQuote.text)}"{validSpeaker(supportingQuote.who) ? ` - ${asText(supportingQuote.who)}` : ''}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: color.ink, marginBottom: 10 }}>2. Why It Matters</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          {hotLayers.length > 0 && (
            <div style={{ border: `1px solid ${color.hairline}`, borderRadius: 10, padding: '12px 14px', background: color.surface }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.purpleDeep, marginBottom: 8 }}>ROOT CAUSE LAYERS</div>
              {hotLayers.slice(0, 4).map((l) => (
                <div key={`${l.code}-${l.name}`} onClick={() => setActive('architecture')} style={{ cursor: 'pointer', border: `1px solid ${color.chipBorder}`, background: color.pillActiveBg, borderRadius: 6, padding: '7px 9px', marginBottom: 6 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: color.purpleDeep }}>{asText(l.name || l.code)}</div>
                  <div style={{ fontSize: 11, color: color.textMuted }}>{l.pct_primary || 0}% of observations</div>
                </div>
              ))}
            </div>
          )}
          {worstStages.length > 0 && (
            <div style={{ border: `1px solid ${color.hairline}`, borderRadius: 10, padding: '12px 14px', background: color.surface }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: '#B91C1C', marginBottom: 8 }}>HIGHEST-FRICTION STAGES</div>
              {worstStages.map((s) => (
                <div key={s.name} onClick={() => setActive('journey')} style={{ cursor: 'pointer', border: '1px solid #FECACA', background: '#FEF2F2', borderRadius: 6, padding: '7px 9px', marginBottom: 6 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#991B1B' }}>{cleanLabel(s.name)}</div>
                  <div style={{ fontSize: 11, color: '#B91C1C' }}>Sentiment {Math.round(normalizeSentiment(s.sentiment) * 100)}%</div>
                </div>
              ))}
            </div>
          )}
          {simplifyItems.length > 0 && (
            <div style={{ border: `1px solid ${color.hairline}`, borderRadius: 10, padding: '12px 14px', background: color.surface }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.purpleDeep, marginBottom: 8 }}>SIMPLIFY FIRST</div>
              {simplifyItems.map((item, idx) => (
                <div key={`${item}-${idx}`} onClick={() => setActive('first-principles')} style={{ cursor: 'pointer', border: `1px solid ${color.chipBorder}`, background: color.pillActiveBg, borderRadius: 6, padding: '7px 9px', marginBottom: 6, fontSize: 12.5, color: color.text }}>
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 17, fontWeight: 700, color: color.ink, marginBottom: 10 }}>3. What To Do</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {quickWins.length > 0 && (
            <div style={{ border: '1px solid #86EFAC', background: '#F0FDF4', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: '#15803D', marginBottom: 8 }}>QUICK WINS</div>
              {quickWins.slice(0, 4).map((o) => (
                <div key={o.title} onClick={() => setActive('insights')} style={{ cursor: 'pointer', background: '#DCFCE7', border: '1px solid #BBF7D0', borderRadius: 6, padding: '7px 9px', marginBottom: 6, fontSize: 12.5, color: '#065F46' }}>
                  {asText(o.title)}
                </div>
              ))}
            </div>
          )}
          {strategicBets.length > 0 && (
            <div style={{ border: `1px solid ${color.chipBorder}`, background: color.pillActiveBg, borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.purpleDeep, marginBottom: 8 }}>STRATEGIC BETS</div>
              {strategicBets.slice(0, 4).map((o) => (
                <div key={o.title} onClick={() => setActive('insights')} style={{ cursor: 'pointer', background: '#EDE9FE', border: `1px solid ${color.chipBorder}`, borderRadius: 6, padding: '7px 9px', marginBottom: 6 }}>
                  <div style={{ fontSize: 12.5, color: color.purpleDeep, fontWeight: 600 }}>{asText(o.title)}</div>
                  <div style={{ fontSize: 11, color: color.textMuted, marginTop: 2 }}>effort {o.effort} · impact {o.impact}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
